import React, { useState } from 'react';

const AgendaProperties = ({ block, onUpdate, groups, seminarDates, defaultDate }) => {
    const [editingEventId, setEditingEventId] = useState(null);
    const [date, setDate] = useState(defaultDate || '');
    const [time, setTime] = useState('09:00');
    const [endTime, setEndTime] = useState('10:00');
    const [title, setTitle] = useState('');
    const [room, setRoom] = useState('');
    const [description, setDescription] = useState('');
    const [eventColor, setEventColor] = useState('#22D6B5');
    const [assignedGroupIds, setAssignedGroupIds] = useState([]);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    const handleGroupToggle = (groupId) => {
        setAssignedGroupIds(prev => 
            prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
        );
    };

    const resetForm = () => {
        setEditingEventId(null);
        setDate(defaultDate || '');
        setTime('09:00');
        setEndTime('10:00');
        setTitle('');
        setRoom('');
        setDescription('');
        setEventColor('#22D6B5');
        setAssignedGroupIds([]);
        setNotificationsEnabled(false);
    };

    const handleSaveEvent = () => {
        if (!title) return;
        const eventData = { date, time, endTime, title, room, description, eventColor, assignedGroupIds, notificationsEnabled };

        if (editingEventId) {
            const updatedContent = block.content.map(event => 
                event.id === editingEventId 
                    ? { ...event, ...eventData } 
                    : event
            );
            onUpdate(block.id, { content: updatedContent });
        } else {
            const newEvent = { id: `event-${Date.now()}`, ...eventData };
            onUpdate(block.id, { content: [...block.content, newEvent] });
        }
        resetForm();
    };

    const handleEditEvent = (eventToEdit) => {
        setEditingEventId(eventToEdit.id);
        setDate(eventToEdit.date);
        setTime(eventToEdit.time);
        setEndTime(eventToEdit.endTime);
        setTitle(eventToEdit.title);
        setRoom(eventToEdit.room);
        setDescription(eventToEdit.description);
        setEventColor(eventToEdit.eventColor);
        setAssignedGroupIds(eventToEdit.assignedGroupIds || []);
        setNotificationsEnabled(eventToEdit.notificationsEnabled || false);
    };

    const handleDeleteEvent = (eventIdToDelete) => {
        const updatedContent = block.content.filter(event => event.id !== eventIdToDelete);
        onUpdate(block.id, { content: updatedContent });
    };

    return (
        <div className="space-y-4">
             <div className="pb-4 border-b space-y-4">
                <h4 className="font-semibold">Style du Texte de l'Agenda</h4>
                <div>
                    <label className="block text-sm font-medium">Titre ({block.titleFontSize || 14}px)</label>
                    <input type="range" min="10" max="24" value={block.titleFontSize || 14} onChange={(e) => onUpdate(block.id, { titleFontSize: parseInt(e.target.value) })} className="w-full" />
                    <input type="color" value={block.titleColor || '#000000'} onChange={(e) => onUpdate(block.id, { titleColor: e.target.value })} className="w-full h-8 mt-1" />
                </div>
                 <div>
                    <label className="block text-sm font-medium">Salle ({block.roomFontSize || 12}px)</label>
                    <input type="range" min="8" max="20" value={block.roomFontSize || 12} onChange={(e) => onUpdate(block.id, { roomFontSize: parseInt(e.target.value) })} className="w-full" />
                    <input type="color" value={block.roomColor || '#555555'} onChange={(e) => onUpdate(block.id, { roomColor: e.target.value })} className="w-full h-8 mt-1" />
                </div>
                 <div>
                    <label className="block text-sm font-medium">Description ({block.descriptionFontSize || 12}px)</label>
                    <input type="range" min="8" max="20" value={block.descriptionFontSize || 12} onChange={(e) => onUpdate(block.id, { descriptionFontSize: parseInt(e.target.value) })} className="w-full" />
                    <input type="color" value={block.descriptionColor || '#333333'} onChange={(e) => onUpdate(block.id, { descriptionColor: e.target.value })} className="w-full h-8 mt-1" />
                </div>
            </div>
            <div className="space-y-2 p-2 border rounded bg-white">
                <h4 className="font-semibold">{editingEventId ? "Modifier l'événement" : "Ajouter un événement"}</h4>
                <div>
                    <label className="block text-sm font-medium">Date de l'événement</label>
                    <select value={date} onChange={e => setDate(e.target.value)} className="w-full p-1 border rounded">
                        {seminarDates.map(d => <option key={d} value={d}>{new Date(d + 'T00:00:00').toLocaleDateString('fr-FR') || 'Date invalide'}</option>)}
                    </select>
                </div>
                <div className="flex gap-2">
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full p-1 border rounded" />
                    <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full p-1 border rounded" />
                </div>
                <input type="text" placeholder="Titre" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-1 border rounded" />
                <input type="text" placeholder="Salle" value={room} onChange={e => setRoom(e.target.value)} className="w-full p-1 border rounded" />
                <textarea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="w-full p-1 border rounded" rows="2"></textarea>
                <input type="color" value={eventColor} onChange={e => setEventColor(e.target.value)} className="w-full h-8" />
                
                <label className="flex items-center mt-2 p-2 bg-gray-50 rounded-md">
                    <input
                        type="checkbox"
                        checked={notificationsEnabled}
                        onChange={(e) => setNotificationsEnabled(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm text-gray-800 flex items-center gap-2">
                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 2a6 6 0 00-6 6v3.586l-1.707 1.707A1 1 0 003 15h14a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                         </svg>
                        Activer la notification push
                    </span>
                </label>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Assigner à des groupes</label>
                    <div className="mt-2 space-y-2">
                        {groups.map(group => (
                            <label key={group.id} className="flex items-center">
                                <input 
                                    type="checkbox" 
                                    checked={assignedGroupIds.includes(group.id)}
                                    onChange={() => handleGroupToggle(group.id)}
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span className="ml-2 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color || '#cccccc' }}></span>
                                    <span className="text-sm text-gray-700">{group.name}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex gap-2 pt-2">
                    <button onClick={handleSaveEvent} className="w-full p-2 bg-blue-500 text-white rounded">{editingEventId ? 'Mettre à jour' : 'Ajouter'}</button>
                    {editingEventId && (
                        <button onClick={resetForm} className="w-full p-2 bg-gray-300 text-black rounded">Annuler</button>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <h4 className="font-semibold mt-4">Événements</h4>
                {block.content.length > 0 ? block.content.map(event => (
                    <div key={event.id} className="p-2 border rounded bg-gray-50 flex justify-between items-center">
                        <div>
                            <p className="font-bold flex items-center gap-2">
                                {event.title}
                                {event.notificationsEnabled && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor" title="Notifications activées">
                                        <path d="M10 2a6 6 0 00-6 6v3.586l-1.707 1.707A1 1 0 003 15h14a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                    </svg>
                                )}
                            </p>
                            <p className="text-sm text-gray-600">{event.time} - {event.endTime}</p>
                        </div>
                        <div className="flex gap-2">
                           <button onClick={() => handleEditEvent(event)} className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-gray-200">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L14.732 3.732z"></path></svg>
                            </button>
                            <button onClick={() => handleDeleteEvent(event.id)} className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-gray-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd"></path></svg>
                            </button>
                        </div>
                    </div>
                )) : (
                    <p className="text-sm text-gray-500">Aucun événement ajouté.</p>
                )}
            </div>
        </div>
    );
};

export default AgendaProperties;